/**
 * Banner displayed when sysadmin is emulating a user.
 *
 * Shows a slim, centered warning pill with emulated user info and end button.
 * It floats just BELOW the app header rather than over it: the header is
 * HEADER_HEIGHT tall and the global search pill is vertically centered inside
 * it, so a pill anchored at the top of the viewport sits exactly on top of the
 * search bar no matter how short it is. Staying under the header keeps the
 * fixed-pill pattern (no disturbance to the app's height-based layout) while
 * leaving every piece of app chrome clickable.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { LtrToken } from './common/LtrToken';
import { useEmulation } from '../contexts/EmulationContext';
import { HEADER_CLEARANCE_TOP } from '../styles/layout.constants';
import { formatCountdown } from '../utils/countdown';
import { cn } from '../../lib/utils';

/** Below this the countdown stops being background information. */
const URGENT_THRESHOLD_MS = 5 * 60_000;

/**
 * Milliseconds left on the session, recomputed every second.
 *
 * The banner used to derive its "40m" from `Date.now()` at render time, and
 * nothing ever re-rendered it — so the pill showed the session's FULL length
 * for the session's whole life and read as a fixed duration rather than as time
 * remaining. The clock has to tick for the number to mean anything.
 *
 * `setInterval` is throttled to roughly once a minute in a backgrounded tab, so
 * the visibility listener re-syncs the moment the operator comes back rather
 * than leaving them looking at a value up to a minute stale — which is the same
 * defect in miniature.
 */
function useMillisecondsRemaining(expiresAt: string | undefined): number {
  const deadline = expiresAt ? new Date(expiresAt).getTime() : 0;
  const [remaining, setRemaining] = useState(() => Math.max(0, deadline - Date.now()));

  useEffect(() => {
    if (!expiresAt) return;

    const sync = () => setRemaining(Math.max(0, deadline - Date.now()));
    sync();

    const interval = window.setInterval(() => {
      sync();
      // Past the deadline the value can no longer change and the banner has
      // already handed over to the expired label, so stop re-rendering — the
      // context's own teardown poll can take up to a minute to arrive, and
      // that whole minute would otherwise be spent ticking a pill that is
      // about to render null.
      if (Date.now() >= deadline) window.clearInterval(interval);
    }, 1000);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [expiresAt, deadline]);

  return remaining;
}

export function EmulationBanner() {
  const { t } = useTranslation('admin');
  const { emulationSession, isEmulating, endEmulation, isEmulationExpired } = useEmulation();
  const millisecondsRemaining = useMillisecondsRemaining(emulationSession?.expires_at);

  if (!isEmulating || !emulationSession) {
    return null;
  }

  const expired = isEmulationExpired();
  const urgent = millisecondsRemaining <= URGENT_THRESHOLD_MS;

  const handleEndEmulation = async () => {
    await endEmulation();
  };

  return (
    <div
      data-testid="emulation-banner"
      style={{ top: HEADER_CLEARANCE_TOP }}
      className={cn(
        'fixed left-1/2 -translate-x-1/2 z-[1300]',
        'flex items-center gap-2 max-w-[95vw]',
        'py-0.5 px-2.5 rounded-md shadow-md',
        expired
          ? 'bg-destructive text-destructive-foreground'
          : 'bg-warning text-warning-foreground'
      )}
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span className="text-xs font-medium truncate min-w-0">
        {emulationSession.emulated_user_name}
      </span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="secondary"
              className="bg-black/15 text-inherit h-4 px-1.5 text-[11px] max-w-[40%] truncate"
            >
              {emulationSession.emulated_tenant_name}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>{emulationSession.emulated_user_email} @ {emulationSession.emulated_tenant_name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {/* At 375px the pill spans the viewport and the emulated user's NAME is
          what must survive, so the least critical token folds away. The expired
          label below never folds. */}
      {!expired && (
        <span
          data-testid="emulation-time-remaining"
          title={t('emulation.banner.timeRemainingLabel')}
          className={cn(
            'text-[11px] whitespace-nowrap hidden sm:inline',
            urgent ? 'font-semibold' : 'opacity-85'
          )}
        >
          <LtrToken>{formatCountdown(millisecondsRemaining)}</LtrToken>
        </span>
      )}
      {expired && (
        <span className="text-[11px] font-bold whitespace-nowrap">
          {t('emulation.banner.expired')}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleEndEmulation}
        className="text-inherit py-0 px-1.5 h-5 text-[11px] shrink-0 hover:bg-black/10"
      >
        {t('emulation.banner.endEmulation')}
      </Button>
    </div>
  );
}
